import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PatternAnalysis = ({ selectedTransaction }) => {
  const [activeTab, setActiveTab] = useState('patterns');

  // Mock pattern analysis data
  const patterns = [
    {
      id: 'pattern1',
      type: 'Fund Splitting',
      confidence: 92,
      description: 'Transaction splits funds into multiple smaller amounts to obscure the trail',
      addresses: ['1F1tAaz5x1HUXrCNLbtMDqcw6o5GNn4xqX', '1Lbcfr7sAHTD9CgdQo3HTMTkV8LK4ZnX71', '1MK2SgwjheyYKuYdgVjjyJFTvJ5wpGmxaj'],
      riskLevel: 'high',
      indicators: [
        'Multiple outputs with similar amounts',
        'Rapid sequential transactions',
        'New addresses with no prior history'
      ]
    },
    {
      id: 'pattern2',
      type: 'Mixing Service',
      confidence: 88,
      description: 'Funds passed through a cryptocurrency mixing service to break transaction linkability',
      addresses: ['1F1tAaz5x1HUXrCNLbtMDqcw6o5GNn4xqX'],
      riskLevel: 'critical',
      indicators: [
        'Known mixer service address',
        'Complex input/output patterns',
        'Time delays between transactions'
      ]
    },
    {
      id: 'pattern3',
      type: 'Exchange Deposit',
      confidence: 75,
      description: 'Funds deposited to a cryptocurrency exchange, potentially for conversion or withdrawal',
      addresses: ['3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy'],
      riskLevel: 'medium',
      indicators: [
        'Known exchange wallet address',
        'Large transaction volume',
        'Multiple deposit patterns'
      ]
    }
  ];

  const suspiciousActivities = [
    {
      id: 'activity1',
      type: 'Rapid Movement',
      timestamp: new Date('2024-09-14T13:20:00Z'),
      description: 'Funds moved through 3 addresses within 10 minutes',
      severity: 'high',
      addresses: 3,
      amount: 17.4
    },
    {
      id: 'activity2',
      type: 'Round Number Transaction',
      timestamp: new Date('2024-09-14T12:15:00Z'),
      description: 'Transaction amount is suspiciously round (8.7 BTC)',
      severity: 'medium',
      addresses: 2,
      amount: 8.7
    },
    {
      id: 'activity3',
      type: 'New Address Usage',
      timestamp: new Date('2024-09-14T11:45:00Z'),
      description: 'Transaction sent to newly created address with no history',
      severity: 'medium',
      addresses: 1,
      amount: 15.2
    }
  ];

  const recommendations = [
    {
      id: 'rec1',
      priority: 'high',
      action: 'Flag Mixer Service',
      description: 'Mark address 1F1tAaz5x1HUXrCNLbtMDqcw6o5GNn4xqX as high-risk mixer service',
      impact: 'Prevents future transactions through this service from being overlooked'
    },
    {
      id: 'rec2',
      priority: 'high',
      action: 'Monitor Split Addresses',
      description: 'Add continuous monitoring to addresses receiving split funds',
      impact: 'Early detection of fund movement attempts'
    },
    {
      id: 'rec3',
      priority: 'medium',
      action: 'Exchange Cooperation',
      description: 'Contact exchange for KYC information on deposit address',
      impact: 'Potential identification of fund recipient'
    },
    {
      id: 'rec4',
      priority: 'low',
      action: 'Pattern Database Update',
      description: 'Update pattern recognition database with new splitting technique',
      impact: 'Improved detection of similar patterns in future cases'
    }
  ];

  const tabs = [
    { id: 'patterns', label: 'Patterns', icon: 'TrendingUp' },
    { id: 'activities', label: 'Suspicious Activities', icon: 'AlertTriangle' },
    { id: 'recommendations', label: 'Recommendations', icon: 'Lightbulb' }
  ];

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical': return 'text-red-700 bg-red-100 border-red-200';
      case 'high': return 'text-error bg-error/10 border-error/20';
      case 'medium': return 'text-warning bg-warning/10 border-warning/20';
      case 'low': return 'text-success bg-success/10 border-success/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error bg-error/10 border-error/20';
      case 'medium': return 'text-warning bg-warning/10 border-warning/20';
      case 'low': return 'text-success bg-success/10 border-success/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const formatAddress = (address) => {
    return `${address?.slice(0, 8)}...${address?.slice(-6)}`;
  };

  return (
    <div className="bg-surface border border-border rounded-lg shadow-forensic-card h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Brain" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-text-primary">Pattern Analysis</h3>
          <span className="text-sm text-text-secondary bg-muted px-2 py-1 rounded">
            AI-Powered Detection
          </span>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex space-x-1 p-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-forensic
                ${activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                }
              `}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {activeTab === 'patterns' && (
          <div className="space-y-4">
            {patterns?.map((pattern) => (
              <div key={pattern?.id} className="border border-border rounded-lg p-4 bg-muted/30">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
                      <Icon name="Target" size={16} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-text-primary">{pattern?.type}</h4>
                      <p className="text-xs text-text-secondary">Confidence: {pattern?.confidence}%</p>
                    </div>
                  </div>
                  <span className={`
                    px-2 py-1 text-xs font-medium rounded-full border
                    ${getRiskColor(pattern?.riskLevel)}
                  `}>
                    {pattern?.riskLevel?.toUpperCase()}
                  </span>
                </div>
                
                <p className="text-sm text-text-secondary mb-3">{pattern?.description}</p>
                
                <div className="space-y-2">
                  <div>
                    <h5 className="text-xs font-semibold text-text-primary mb-1">Involved Addresses:</h5>
                    <div className="flex flex-wrap gap-1">
                      {pattern?.addresses?.map((address, index) => (
                        <span key={index} className="text-xs font-data bg-surface px-2 py-1 rounded border">
                          {formatAddress(address)}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-xs font-semibold text-text-primary mb-1">Key Indicators:</h5>
                    <ul className="text-xs text-text-secondary space-y-1">
                      {pattern?.indicators?.map((indicator, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <Icon name="ChevronRight" size={12} />
                          <span>{indicator}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'activities' && (
          <div className="space-y-4">
            {suspiciousActivities?.map((activity) => (
              <div key={activity?.id} className="border border-border rounded-lg p-4 bg-muted/30">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-warning/10 rounded-lg">
                      <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-text-primary">{activity?.type}</h4>
                      <p className="text-xs text-text-secondary">
                        {activity?.timestamp?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <span className={`
                    px-2 py-1 text-xs font-medium rounded-full border
                    ${getRiskColor(activity?.severity)}
                  `}>
                    {activity?.severity?.toUpperCase()}
                  </span>
                </div>
                
                <p className="text-sm text-text-secondary mb-3">{activity?.description}</p>
                
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-text-secondary">Addresses Involved:</span>
                    <span className="ml-2 font-semibold text-text-primary">{activity?.addresses}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary">Amount:</span>
                    <span className="ml-2 font-semibold text-text-primary">{activity?.amount} BTC</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div className="space-y-4">
            {recommendations?.map((rec) => (
              <div key={rec?.id} className="border border-border rounded-lg p-4 bg-muted/30">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-accent/10 rounded-lg">
                      <Icon name="Lightbulb" size={16} color="var(--color-accent)" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-text-primary">{rec?.action}</h4>
                    </div>
                  </div>
                  <span className={`
                    px-2 py-1 text-xs font-medium rounded-full border
                    ${getPriorityColor(rec?.priority)}
                  `}>
                    {rec?.priority?.toUpperCase()}
                  </span>
                </div>
                
                <p className="text-sm text-text-secondary mb-2">{rec?.description}</p>
                
                <div className="bg-surface border border-border rounded p-2">
                  <p className="text-xs text-text-secondary">
                    <span className="font-semibold">Impact:</span> {rec?.impact}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer Actions */}
      <div className="p-4 border-t border-border bg-muted">
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            Analysis updated: {new Date()?.toLocaleString()}
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Icon name="Download" size={16} className="mr-2" />
              Export Analysis
            </Button>
            <Button variant="default" size="sm">
              <Icon name="FileText" size={16} className="mr-2" />
              Generate Report
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatternAnalysis;